import {
    Card,
    Row,
} from "react-bootstrap";
import React, { useContext, useEffect, useState } from "react";
import { FixedSizeList as List } from "react-window";
import { Checkbox } from "rsuite";
import axios from "../../utils/axiosConfig";
import Moment from "react-moment";
import SitewideDataContext from "../../utils/sitewideContext";
import {useAuth0} from "@auth0/auth0-react";
import {Buffer} from 'buffer';
import * as Sentry from "@sentry/react";
import "../../constants/service_constants";

Moment.globalFormat = 'YYYY-MM-DD HH:mm:ss';

function HistoryCard({setNewHTMLSourceURLs, start_time, end_time, siteDomain}) {
    const { getAccessTokenSilently } = useAuth0();
    const [data, setData] = useState([]);
    const [selectedRecordsCount, setSelectedRecordsCount] = useState(0);
    const {sitewideData, setSitewideData} = useContext(SitewideDataContext);

    let queryParameters = new URLSearchParams(window.location.search);

    const site_id = queryParameters.get("site_id");
    const page_id = decodeURIComponent(queryParameters.get("page_id"));
    var str_page_id = Buffer.from(page_id).toString('base64');

    const handleDisableCheckboxes = () => {
        setData(oldData => {
            return oldData.map(record => {
                if (selectedRecordsCount >= 2 && !record.selected) {
                    return { ...record, disabled: true };
                } else {
                    return { ...record, disabled: false };
                }
            });
        });
    };

    const handleSetHTMLSourceURLs = () => {
        try {
            let queryParameters = new URLSearchParams(window.location.search);
            const selectedRecords = data.filter(record => record.selected);
            if (selectedRecords.length < 2) {
                setNewHTMLSourceURLs(
                    selectedRecords[0].html_source,
                    null,
                    selectedRecords[0].start_time,
                    null);
                queryParameters.set("starttime", selectedRecords[0].start_time);
                queryParameters.set("endtime", 'null');
            } else {
                setNewHTMLSourceURLs(
                    selectedRecords[0].html_source,
                    selectedRecords[1].html_source,
                    selectedRecords[0].start_time,
                    selectedRecords[1].start_time);
                queryParameters.set("starttime", selectedRecords[0].start_time);
                queryParameters.set("endtime", selectedRecords[1].start_time);
            }
            window.history.pushState({}, "", "?" + queryParameters.toString());
        } catch (e) {
            if (typeof Sentry !== 'undefined') {
                try { Sentry.captureException(e); } catch (_) {}
            }
        }
    }

    function handleSelect(startTime) {
        setData(prevData => {
            const newData = prevData.map(record => {
                if (record.start_time === startTime && !record.disabled) {
                    return { ...record, selected: !record.selected };
                } else {
                    return record;
                }
            });
            setSelectedRecordsCount(newData.filter(record => record.selected).length);
            return newData;
        });
    }

    useEffect(() => {
        handleDisableCheckboxes();
        if (selectedRecordsCount > 0 && selectedRecordsCount < 3) {
            handleSetHTMLSourceURLs();
        }
    }, [selectedRecordsCount]);

    const handleCheckboxChange = (event, other, syntheticEvent) => {
        const newSelected = syntheticEvent.target.checked;
        const startTime = syntheticEvent.target.id;
        setData(prevData => {
            const newData = prevData.map(record => {
                if (record.start_time.toString() === startTime) {
                    return { ...record, selected: newSelected };
                } else {
                    return record;
                }
            });
            setSelectedRecordsCount(newData.filter(record => record.selected).length);
            return newData;
        });

        syntheticEvent.stopPropagation();
    };

    useEffect(() => {
        async function getData() {
            setData([]);
            const accessToken = await getAccessTokenSilently();
            const historyResponse = await axios.get(
                API_ADDRESS + PAGE_HISTORY_ENDPOINT + site_id + "/" + str_page_id,
                {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const pageHistory = historyResponse.data.page_history || [];
            const newData = pageHistory.map((record, key) => {
                const page_crawl = {
                    html_length: record.pages[str_page_id]?.html_length || '',
                    selected: false,
                    disabled: false,
                    html_source: record.pages[str_page_id]?.html_location,
                    screenshot: record.pages[str_page_id]?.screenshot_location,
                    start_time: record.start_time,
                };
                if (end_time) {
                    if (record.start_time === end_time) {
                        page_crawl.selected = true;
                        page_crawl.disabled = false;
                    }
                    if (start_time && record.start_time === start_time) {
                        page_crawl.selected = true;
                        page_crawl.disabled = false;
                    }
                } else {
                    page_crawl.selected = key < 1;
                    page_crawl.disabled = key >= 1;
                }
                return page_crawl;
            });
            setSelectedRecordsCount(newData.filter(r => r.selected).length);
            setData(newData);
        }

        getData().finally(() => {
            setSitewideData({
                ...sitewideData,
                overloading: false
            })
        });
    }, []);

    return (
        <Card className="page-history-card">
            <Card.Header>
                <Card.Title as="h4">History</Card.Title>
            </Card.Header>
            <Card.Body>
                <p className="page-history-card__hint text-muted small mb-2">Select a crawl or compare two:</p>
                <div className="site-history-card__list">
                    <List
                        height={Math.min(700, data.length * 72 + 20)}
                        itemCount={data.length}
                        itemSize={72}
                        width="100%"
                        style={{ overflowX: 'hidden' }}
                    >
                        {({ index, style }) => {
                            const record = data[index];
                            return (
                                <div style={style} key={record.start_time}>
                                    <Row>
                                        <div
                                            className={`site-history-card__item ${record.selected ? 'site-history-card__item--selected' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleSelect(record.start_time);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleSelect(record.start_time);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <Checkbox
                                                id={record.start_time}
                                                disabled={record.disabled}
                                                checked={record.selected}
                                                onChange={handleCheckboxChange}
                                            >
                                                <Moment unix className="text-muted my-2 site-history-card__item-date">
                                                    {record.start_time}
                                                </Moment>
                                                <br />
                                                <small className="text-muted">Size: {record.html_length ? record.html_length : 'n/a'} chars</small>
                                            </Checkbox>
                                        </div>
                                    </Row>
                                </div>
                            );
                        }}
                    </List>
                </div>
            </Card.Body>
            <Card.Footer>
            </Card.Footer>
        </Card>
    );
}

export default HistoryCard;